import React, { useContext } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import { LocaleContext } from 'routes/Router';

const PhoneNumberContainer = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 20px;
`;

const PhoneLabel = styled.label`
  color: #111827;

  font-family: ${(props) => props.labelFontFamily || 'Akkurat'};

  font-size: ${(props) => props.labelFontSize || '16px'};

  font-weight: ${(props) => props.labelFontWeight || '500'};

  line-height: ${(props) => props.labelLineHeight || '24px'};

  letter-spacing: ${(props) => props.labelLetterSpacing || '0px'};

  margin-bottom: 8px;
`;

const PhoneInputWrapper = styled.div`
  display: flex;
  align-items: center;
`;

const CountryCodeInput = styled.div`
  margin-right: ${(props) => (props?.locale === 'en' ? '20px' : '')};
  margin-left: ${(props) => (props?.locale === 'ar' ? '20px' : '')};
  flex-shrink: 0;

  .form-control.custom-country-input {
    width: ${(props) => props.countryWidth || '100px'} !important;
    height: ${(props) => props.height || '48px'} !important;

    border-radius: ${(props) => props.borderRadius || '16px'} !important;

    border: 1px solid ${(props) => props.borderColor || '#D6D3D1'} !important;

    background: #ffffff !important;

    font-family: ${(props) => props.fontFamily || 'Akkurat'} !important;

    font-size: ${(props) => props.fontSize || '16px'} !important;
    font-weight: ${(props) => props.fontWeight || '500'} !important;
    line-height: 24px !important;

    color: ${(props) => props.textColor || '#111827'} !important;

    margin-left: 0 !important;
  }

  .react-tel-input {
    .form-control {
      height: ${(props) => props.height || '48px'} !important;

      padding-left: 60px !important;

      border-radius: ${(props) => props.borderRadius || '16px'} !important;

      border: 1px solid ${(props) => props.borderColor || '#D6D3D1'} !important;

      font-family: ${(props) => props.fontFamily || 'Akkurat'} !important;
      font-size: ${(props) => props.fontSize || '16px'} !important;
      font-weight: ${(props) => props.fontWeight || '500'} !important;
      line-height: 24px !important;
    }

    .form-control:hover {
      border-color: ${(props) =>
        props.hoverBorderColor || '#BFBAB7'} !important;
    }

    .form-control:focus {
      outline: none;
      box-shadow: none;
      border-color: ${(props) =>
        props.focusBorderColor || '#111111'} !important;
    }

    .flag-dropdown {
      border-radius: ${(props) => props.borderRadius || '16px'} !important;

      background-color: #fff !important;

      border: 1px solid ${(props) => props.borderColor || '#D6D3D1'} !important;
    }

    .selected-flag {
      position: relative;
      padding-left: 0px;
      width: 52px !important;
      border-radius: ${(props) => props.borderRadius || '16px'};
    }

    .selected-flag .flag {
      margin-left: ${(props) => (props?.locale === 'en' ? '20px' : '')};

      margin-right: ${(props) => (props?.locale === 'ar' ? '30px' : '')};
    }
  }
`;

const PhoneNumberInputWrapper = styled.div`
  flex: 1;
  min-width: 0;

  .input-with-suffix {
    position: relative;
    display: flex;
    align-items: center;

    input {
  width: 100%;
  height: ${(props) => props.height || '48px'};

  padding: 0 16px;
  padding-right: 40px;

  border: 1px solid ${(props) => props.borderColor || '#D6D3D1'};
  border-radius: ${(props) => props.borderRadius || '16px'};

  background: #ffffff;

  color: ${(props) => props.textColor || '#111827'};

  font-family: ${(props) => props.fontFamily || 'Akkurat'};
  font-size: ${(props) => props.fontSize || '16px'};
  font-weight: ${(props) => props.fontWeight || '500'};
  line-height: 24px;   /* add this */

  outline: none;
  box-shadow: none;

  transition: all 0.2s ease;
  }
`;

const ErrorMessage = styled.p`
  font-size: 16px;
  color: #ee3232;
  line-height: 24px;
  font-weight: 400;
  font-family: Akkurat;
  margin-top: 5px;
`;

const PhoneNumberInput = ({
  label,
  country,
  value,
  name,
  required,
  onCountryCodeChange,
  onPhoneNumberChange,
  phoneNumber,
  phoneNumberRef,
  placeholder,
  isPhoneNumberValid,
  maxLength,
  isError,
  errorMessage,
  onlyCountries,
  suffixText,
  disabled = false,
  height,
  borderRadius,
  borderColor,
  focusBorderColor,
  hoverBorderColor,
  errorBorderColor,

  fontFamily,
  fontSize,
  fontWeight,
  textColor,

  placeholderColor,
  placeholderFontSize,

  labelFontFamily,
  labelFontSize,
  labelFontWeight,
  labelLineHeight,
  labelLetterSpacing,

  countryWidth,
}) => {
  const { locale } = useContext(LocaleContext);
  return (
    <div className="relative">
      <PhoneNumberContainer>
        {label && (
          <PhoneLabel
            labelFontFamily={labelFontFamily}
            labelFontSize={labelFontSize}
            labelFontWeight={labelFontWeight}
            labelLineHeight={labelLineHeight}
            labelLetterSpacing={labelLetterSpacing}
          >
            {label}
            {required && <span style={{ color: 'red' }}> *</span>}
          </PhoneLabel>
        )}
        <PhoneInputWrapper>
          <CountryCodeInput
            locale={locale}
            height={height}
            borderRadius={borderRadius}
            borderColor={borderColor}
            focusBorderColor={focusBorderColor}
            hoverBorderColor={hoverBorderColor}
            fontFamily={fontFamily}
            fontSize={fontSize}
            fontWeight={fontWeight}
            textColor={textColor}
          >
            <PhoneInput
              country={country}
              value={value}
              name={name}
              onChange={onCountryCodeChange}
              inputProps={{
                name: 'countryCode',
                required: true,
              }}
              placeholder="country"
              inputClass="custom-country-input"
              disableCountryCode
              disableDropdown={false}
              onlyCountries={onlyCountries}
              disabled={disabled}
            />
          </CountryCodeInput>

          <PhoneNumberInputWrapper
            height={height}
            borderRadius={borderRadius}
            borderColor={borderColor}
            focusBorderColor={focusBorderColor}
            hoverBorderColor={hoverBorderColor}
            errorBorderColor={errorBorderColor}
            fontFamily={fontFamily}
            fontSize={fontSize}
            fontWeight={fontWeight}
            textColor={textColor}
            placeholderColor={placeholderColor}
            placeholderFontSize={placeholderFontSize}
          >
            <div className="input-with-suffix">
              <input
                type="text"
                name={name}
                value={phoneNumber}
                onChange={onPhoneNumberChange}
                ref={phoneNumberRef}
                placeholder={placeholder}
                inputMode="numeric"
                maxLength={10}
                className={!isPhoneNumberValid ? 'invalid' : ''}
                disabled={disabled}
              />
              {suffixText && <span className="input-suffix">{suffixText}</span>}
            </div>
          </PhoneNumberInputWrapper>
        </PhoneInputWrapper>

        {isError ? (
          <ErrorMessage>{isError}</ErrorMessage>
        ) : (
          !isPhoneNumberValid && (
            <ErrorMessage>
              {errorMessage || 'Invalid phone number for selected country code'}
            </ErrorMessage>
          )
        )}
      </PhoneNumberContainer>
    </div>
  );
};

PhoneNumberInput.propTypes = {
  label: PropTypes.node,
  country: PropTypes.string.isRequired,
  value: PropTypes.string.isRequired,
  name: PropTypes.string.isRequired,
  onCountryCodeChange: PropTypes.func.isRequired,
  onPhoneNumberChange: PropTypes.func.isRequired,
  phoneNumber: PropTypes.string.isRequired,
  phoneNumberRef: PropTypes.object,
  placeholder: PropTypes.string,
  isPhoneNumberValid: PropTypes.bool.isRequired,
  isError: PropTypes.string,
  errorMessage: PropTypes.string,
  onlyCountries: PropTypes.arrayOf(PropTypes.string),
  suffixText: PropTypes.string,
  height: PropTypes.string,
  borderRadius: PropTypes.string,
  borderColor: PropTypes.string,
  focusBorderColor: PropTypes.string,
  hoverBorderColor: PropTypes.string,
  errorBorderColor: PropTypes.string,

  fontFamily: PropTypes.string,
  fontSize: PropTypes.string,
  fontWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  textColor: PropTypes.string,

  placeholderColor: PropTypes.string,
  placeholderFontSize: PropTypes.string,

  labelFontFamily: PropTypes.string,
  labelFontSize: PropTypes.string,
  labelFontWeight: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  labelLineHeight: PropTypes.string,
  labelLetterSpacing: PropTypes.string,

  countryWidth: PropTypes.string,
};

PhoneNumberInput.defaultProps = {
  label: null,
  phoneNumberRef: null,
  placeholder: 'e.g: 8521364783',
  isError: null,
  errorMessage: null,
  onlyCountries: ['qa', 'us', 'in', 'gb', 'sa', 'jo', 'ae', 'kw'],
  suffixText: null,

  height: '48px',
  borderRadius: '16px',
  borderColor: '#D6D3D1',
  focusBorderColor: '#111111',
  hoverBorderColor: '#BFBAB7',
  errorBorderColor: '#EE3232',

  fontFamily: 'Akkurat',
  fontSize: '16px', // changed from 15px
  fontWeight: '500', // changed from 400
  textColor: '#111827',

  placeholderColor: '#9CA3AF',
  placeholderFontSize: '14px',

  labelFontFamily: 'Akkurat',
  labelFontSize: '16px',
  labelFontWeight: '500',
  labelLineHeight: '24px',
  labelLetterSpacing: '0px',

  countryWidth: '100px',
};

export default PhoneNumberInput;
